import React from "react";
import { defaultIconProps, IconProps, sizeMap } from "./types";

// Custom inline-SVG glyphs for the five One Piece TCG card supertypes.
// Stroke-based to match Lucide's house style so they sit cleanly next to
// existing section icons (Crown, ArrowUpDown, HelpCircle). All accept the
// project's standard IconProps and theme via `currentColor`.

interface InternalProps extends IconProps {
  children: React.ReactNode;
  ariaLabel: string;
}

function IconShell({
  size = defaultIconProps.size,
  color = defaultIconProps.color,
  className = "",
  fontSize,
  children,
  ariaLabel,
}: InternalProps) {
  const pixelSize = fontSize ?? sizeMap[size as keyof typeof sizeMap];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={pixelSize}
      height={pixelSize}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={ariaLabel}
      className={`inline-block shrink-0 ${className}`}
    >
      {children}
    </svg>
  );
}

// Three-peak crown with a base bar — distinct from Lucide's Crown so the
// OP Leader slot reads as its own thing alongside the (still-Lucide) MTG
// Commander icon.
export const LeaderIcon = (props: IconProps) => (
  <IconShell {...props} ariaLabel="Leader">
    <path d="M3 18 L5 7 L9 11 L12 4 L15 11 L19 7 L21 18 Z" />
    <line x1="3.5" y1="21" x2="20.5" y2="21" />
    <circle cx="12" cy="13" r="0.6" fill={props.color ?? "currentColor"} />
  </IconShell>
);

// Compact bust silhouette: head + shoulders. Universal "person" read.
export const CharacterIcon = (props: IconProps) => (
  <IconShell {...props} ariaLabel="Character">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5.5 20 a6.5 6.5 0 0 1 13 0" />
  </IconShell>
);

// Chunky 8-point comic burst — alternating outer/inner radii give it the
// "Wham!" silhouette without being mistaken for a sun or sparkle. Filled
// so it pops at small sizes against the line-only siblings.
export const EventIcon = (props: IconProps) => (
  <IconShell {...props} ariaLabel="Event">
    <path
      d="M12 2.5 L13.6 8.4 L19.2 5.4 L17.1 11 L22 12 L17.1 13 L19.2 18.6 L13.6 15.6 L12 21.5 L10.4 15.6 L4.8 18.6 L6.9 13 L2 12 L6.9 11 L4.8 5.4 L10.4 8.4 Z"
      fill={props.color ?? "currentColor"}
    />
  </IconShell>
);

// Wavy pennant on a vertical pole. Pirates plant flags — fits the "this
// is your territory" feel of OP Stage cards.
export const StageIcon = (props: IconProps) => (
  <IconShell {...props} ariaLabel="Stage">
    <path d="M5 3 L5 21" />
    <path d="M5 4 Q9 2 13 4 Q17 6 19 4 L19 12 Q15 14 11 12 Q7 10 5 12" />
  </IconShell>
);

// Coin/medallion with two exclamation strokes — the iconic "DON!!"
// branding distilled to readable glyph form.
export const DonIcon = (props: IconProps) => (
  <IconShell {...props} ariaLabel="DON!!">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 7 L9 13" />
    <path d="M15 7 L15 13" />
    <circle cx="9" cy="16.5" r="0.7" fill={props.color ?? "currentColor"} />
    <circle cx="15" cy="16.5" r="0.7" fill={props.color ?? "currentColor"} />
  </IconShell>
);
